import { HttpClient } from '@angular/common/http';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { CustomColDef } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';


export enum BiosampleLibraryPrepSearchColumnFields {
    BioSampleName = 'BioSampleName',
    PrepKitName = 'PrepKitName',
    DefaultProjectName = 'DefaultProjectName'
}

export class BiosampleLibraryPrepSearchGridOptionsFormatter extends SearchGridOptionsFormatter<BiosampleLibraryPrepSearchColumnFields> {
    public projectFilterOptions$: Observable<FilterOption[]>;
    public projectColumnFilterOptionsUpdateCallback;

    constructor(
      public resourceDictionaryService: SearchResourceDictionaryService, 
      public bsshDatePipe: BsshDatePipe,
      public http: HttpClient
    ) {
        super(resourceDictionaryService, SearchResourceType.BiosampleLibraryPrep);
        this.bindFilterUpdateCallbacksToCurrentContext();
        this.projectFilterOptions$ = this.updateProjectColumnFilterOptions();
    }

    getProjectFilterOptions(arg?: string): Observable<any> {
      return this.http.get(BsApiEndPoints.SEARCH_FILTER_URL_V1PRE3, {
          params: {
              limit: '50',
              scope: 'projects',
              sortBy: 'ModifiedOn',
              sortDir: 'desc',
              query: arg ? `Project.Name:"${arg}"` : ''
          },
          withCredentials: true
      });
  }

  updateProjectColumnFilterOptions(arg?: string): Observable<FilterOption[]> {
      return this.getProjectFilterOptions(arg).pipe(
        map((response) => {
          return this.toProjectFilterOptions(response);
        })
      );
  }

  bindFilterUpdateCallbacksToCurrentContext() {
      this.projectColumnFilterOptionsUpdateCallback = this.updateProjectColumnFilterOptions.bind(this);
  }

  toProjectFilterOptions(result): FilterOption[] {
      const projects: [] = result.Response.Items;
      const filteroptions: FilterOption[] = [];
      projects.forEach((item: any) => {
        const filterOption: FilterOption = {
          displayName: item.Project.Name,
          filterValue: item.Project.Id
        };
        filteroptions.push(filterOption);

      });
      return filteroptions;
  }

    protected getOverrideColumnParameters(): OverrideColumnParameters<BiosampleLibraryPrepSearchColumnFields> {
        const inclusiveColumns: BiosampleLibraryPrepSearchColumnFields[] = [
            BiosampleLibraryPrepSearchColumnFields.BioSampleName,
            BiosampleLibraryPrepSearchColumnFields.PrepKitName,
            BiosampleLibraryPrepSearchColumnFields.DefaultProjectName
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<BiosampleLibraryPrepSearchColumnFields>[] = [
            {
              colId: BiosampleLibraryPrepSearchColumnFields.BioSampleName,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions(): CustomColDef[] {
        return [
            {
                headerName: 'Biosample Name',
                field: 'BioSampleLibraryPrep.BioSampleName',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data && params.data.BioSampleLibraryPrep) {
                        // Extract biosample ID from composite ID (format: "biosampleId::prepId")
                        const biosampleId = params.data.BioSampleLibraryPrep.Id.split('::')[0];
                        return DataTableUtilities.getRadLinkParams(
                            params.data.BioSampleLibraryPrep.BioSampleName,
                            `/biosamples/${biosampleId}`
                        );
                    } else {
                        return {};
                    }
                },
                flex: 3,
                filterActive: false,
                sortable: false,
                colId: BiosampleLibraryPrepSearchColumnFields.BioSampleName,
            },
            {
                headerName: 'Prep Kit Name',
                field: 'BioSampleLibraryPrep.PrepKitName',
                flex: 3,
                filterActive: false,
                sortable: false,
                colId: BiosampleLibraryPrepSearchColumnFields.PrepKitName,
                cellRenderer: 'span',
            },
            {
                headerName: 'Project',
                field: 'BioSampleLibraryPrep.DefaultProject.Name',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data && params.data.BioSampleLibraryPrep && params.data.BioSampleLibraryPrep.DefaultProject) {
                        const project = params.data.BioSampleLibraryPrep.DefaultProject;
                        return DataTableUtilities.getRadLinkParams(
                            project.Name,
                            `/projects/${project.Id}`
                        );
                    } else {
                        return {};
                    }
                },
                colId: BiosampleLibraryPrepSearchColumnFields.DefaultProjectName,
                sortable: false,
                flex: 3,
                filterActive: true,
                filterWithSearchBox: true,
                filterOptions: this.projectFilterOptions$,
                updateFilterOptionsCallback: this.projectColumnFilterOptionsUpdateCallback
            }
        ];
    }
}

